/**
 * File: apps/qbench-cp/reactapp/src/shared/configs/normalizer/strategies.js
 *
 * Purpose: Provides normalizr `processStrategy` functions that reshape raw entity data during
 *     normalization.
 *
 * Scope: Per-entity-type normalization strategies (order, sample, test, assay) consumed by the
 *     schemas in `./schemas.js`.
 *
 * Tags: customer-portal, field-config
 *
 * Overview: Exports a `strategies` object with `merge`, `process`, and `fallback` buckets; only
 *     `process` is populated. Order and sample strategies tag the entity with a `_qbDataType`
 *     marker. The test strategy adds `_qbDataType` and converts `state` into a `status`
 *     `{value, label}` pair, omitting the original `state` key. The assay strategy skips panel
 *     entities (via `isPanelType`), and for non-panel assays parses the `default_to_test_config`
 *     JSON field, processes it through the test entity's field pipeline, and merges the results
 *     into each test field's `portalDefaultValue` as `_defaultToTestFields`; per repo notes, this
 *     computed field is not consumed by the test form pipeline.
 *
 * Exports: strategies (default export) — process-strategy functions keyed by EntityType.
 */

import { getFields, processEntity } from 'context/fields';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { EntityType } from 'shared/constants/entityTypes';
import { safeJsonParse } from 'shared/utils/functions';
import { isPanelType } from 'shared/utils/misc';

const strategies = {
  merge: {},
  process: {
    [EntityType.ORDER]: (entity) => {
      return {
        _qbDataType: EntityType.ORDER,
        ...entity,
      };
    },

    [EntityType.SAMPLE]: (entity, parent, key) => {
      return {
        _qbDataType: EntityType.SAMPLE,
        ...entity,
      };
    },

    [EntityType.TEST]: (entity) => {
      return omit(
        {
          _qbDataType: EntityType.TEST,
          ...entity,
          status: {
            value: entity.state || null,
            label: entity.state || '',
          },
        },
        ['state'],
      );
    },

    [EntityType.ASSAY]: (entity) => {
      if (isPanelType(entity)) return entity;

      const entityType = EntityType.TEST;
      const testFields = getFields({ entityType });
      const { default_to_test_config: configJson, ...assay } = entity;
      const defaultToTestObj = isEmpty(configJson)
        ? {}
        : processEntity({ entity: safeJsonParse(configJson), entityType });
      const _defaultToTestFields = testFields.map((field) => ({
        ...field,
        portalDefaultValue:
          defaultToTestObj[field.name] || field.portalDefaultValue,
      }));

      return {
        ...assay,
        _defaultToTestFields,
      };
    },
  },
  fallback: {},
};

export default strategies;
